import {
  buildConventionalScopeBody,
  ConventionalScope,
  conventionalScopeKey,
  coreApi,
  BlueprintFamilyMonthlyTrendsItem,
  BlueprintFamilyTrendsWindowDays,
} from '../core'

// Response shape for POST /blueprint-families/trends/event-types: the shared monthly
// batched-trends item, where `id` is the event-type id (e.g. "Battle Pass") rather than
// a blueprint or event-group id. The series is type-scoped — every game running any
// event of the type, whether or not its blueprint was catalogued into a family — so it
// reconciles with `distinctGamesCount` on the matching rollup row.
export type BlueprintFamilyEventTypeTrendsApiItem = BlueprintFamilyMonthlyTrendsItem

// Query args for the batched event-type trends request. `eventTypeIds` accepts 1–10 ids.
//
// The id field name, not a `groupBy` param, is what selects the endpoint: the BE checks
// each id against the kind of thing the field promises, so passing an event-group id
// here returns a 400 naming the offending field and id rather than silently trending the
// wrong entity.
type GetBlueprintFamilyEventTypeTrendsQueryParams = {
  eventTypeIds: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const eventTypeTrendsApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlueprintFamilyEventTypeTrends: builder.query<BlueprintFamilyEventTypeTrendsApiItem[], GetBlueprintFamilyEventTypeTrendsQueryParams>({
      // POST-with-body modeled as a query (read-only fan-out), mirroring the sibling
      // event-group endpoint.
      query: ({ eventTypeIds, windowDays, ...scope }) => ({
        url: 'blueprint-families/trends/event-types',
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { eventTypeIds, ...buildConventionalScopeBody(scope) },
      }),
      // Reuse the shared blueprint cache tag; encode the request shape so distinct
      // id-sets / windows / scopes cache independently.
      providesTags: (_result, _err, { eventTypeIds, windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `trends/event-types/${[...eventTypeIds].sort().join(',')}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyEventTypeTrendsQuery } = eventTypeTrendsApi
